<template>
  <div
    class="block-auth-user-leaderboard"
    :class="{
      'has-logged-in-user': showLoggedInUser,
      'has-no-logged-in-user': !showLoggedInUser,
    }"
  >
    <div
      v-if="filterTabs?.length > 0"
      class="block-auth-user-leaderboard__filters"
    >
      <BlockFilterTabs
        :tabs="filterTabs"
        :initial-active-tab="filterTabs[0].name"
        @on-tab-click="handleFilter"
      />
    </div>
    <div class="block-auth-user-leaderboard__list">
      <div v-if="!showLoggedInUser" class="block-auth-user-leaderboard__fixed-header">
        <div
          v-for="(column, columnIndex) in columns"
          :key="`leaderboard-entry-column-${componentUid + columnIndex}`"
          class="block-auth-user-leaderboard__list-column"
          :style="`--column-width: ${column.width};`"
        >
          <div class="block-auth-user-leaderboard__header-fixed">
            {{ column.label }}
          </div>
        </div>
      </div>

      <transition-group name="list-leaderboard">
        <div
          v-for="(topuser, index) in users"
          :key="`leaderboard-top-user-${componentUid}-${topuser.username}`"
          class="block-auth-user-leaderboard__entry"
          :class="{
            'is-logged-in-user': topuser.isMe,
          }"
        >
          <div
            v-for="(column, columnIndex) in columns"
            :key="`leaderboard-entry-column-${componentUid + columnIndex}`"
            class="block-auth-user-leaderboard__list-column"
            :style="`--column-width: ${column.width};`"
          >
            <div v-if="showLoggedInUser" class="block-auth-user-leaderboard__header">
              {{ column.label }}
            </div>

            <div class="block-auth-user-leaderboard__value">
              <span
                v-if="column.content === '{{username}}'
                  && topuser.isMe && index === users.length - 1 && showLoggedInUser"
              >{{ loggedinUsername }}</span>
              <span v-else-if="showClearnames">
                {{ compileWithHandlebars(column.clearname || column.content, topuser) }}
              </span>
              <span v-else>
                {{ compileWithHandlebars(column.content, topuser) }}
              </span>
            </div>
          </div>
        </div>
      </transition-group>
    </div>
  </div>
</template>

<script setup>
defineProps({
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    columns: {
        type: Array,
        required: true,
    },

    filterTabs: {
        type: Array,
        default: () => [],
    },

    users: {
        type: Array,
        default: () => [],
    },

    showLoggedInUser: {
        type: Boolean,
        default: true,
    },

    showClearnames: {
        type: Boolean,
        default: false,
    },

    loggedinUsername: {
        type: String,
        default: 'Du',
    },
});

const componentUid = getUid();

const emit = defineEmits(['on-filter']);
const handleFilter = (event) => {
    emit('on-filter', event);
    // useStorybookAction('on-filter', event);
};
</script>

<style lang="scss" scoped>
.block-auth-user-leaderboard {
    @include fluid('font-size', 15px, 12px, 15px);
    @include fluid('line-height', 18px, 17px, 18px);

    --horizonal-padding: 0;
    width: calc(100% + var(--horizontal-padding) * 2);
    padding-right: var(--horizontal-padding);
    padding-left: var(--horizontal-padding);
    font-family: var(--f-family--primary);
    font-size: 15px;
    font-weight: var(--f-weight--400);
    transform: translateX(calc(var(--horizontal-padding) * -1));

    @include tablet {
        @include fluid('--horizontal-padding', 0px, 20px, 37px);
    }
}

.block-auth-user-leaderboard__filters {
    margin-bottom: 10px;
}

.block-auth-user-leaderboard__header {
    display: flex;
    align-items: center;
    color: var(--c-primary);
    font-weight: var(--f-weight--900);
    white-space: nowrap;

    @include tablet {
        @include fluid('padding-top', 0px, 5px, 11px);
        @include fluid('padding-bottom', 0px, 5px, 11px);

        display: none;
    }
}

.block-auth-user-leaderboard__fixed-header {
    @include grid-layout(1, 12, 12);

    @include fluid('padding-bottom', 20px, 5px, 11px);

    position: relative;
    font-weight: var(--f-weight--900);
    row-gap: 20px;
}

.block-auth-user-leaderboard__entry {
    @include grid-layout(1, 12, 12);
    @include fluid('padding-top', 20px, 5px, 11px);
    @include fluid('padding-bottom', 20px, 5px, 11px);

    position: relative;
    border-bottom: 1px solid var(--c-primary);
    row-gap: 20px;

    &:after {
        position: absolute;
        z-index: -1;
        right: 0;
        bottom: 0;
        left: calc(var(--horizontal-padding) * -1);
        width: calc(100% + var(--horizontal-padding) * 2);
        height: calc(100% - 2px);
        border-radius: var(--b-radius--medium);
        content: '';
        pointer-events: none;
        transition: background-color 0.25s ease-in-out;

        @include tablet {
            @include fluid('height', 27px, 27px, 41px);
        }
    }
    &:first-child {
        .block-auth-user-leaderboard__header {
            @include tablet {
                display: block;
            }
        }
    }

    &:last-child {
        padding: 20px;
        border-radius: var(--b-radius--medium);
        border-bottom: none;
        margin-top: 20px;
        background: var(--c-secondary);

        .has-no-logged-in-user & {
            padding-right: 0;
            padding-left: 0;
            margin-top: 0;
            background: transparent;
        }

        @include tablet {
            @include fluid('padding-top', 20px, 5px, 11px);
            @include fluid('padding-bottom', 20px, 5px, 11px);

            padding-right: 0;
            padding-left: 0;
            border-radius: 0;
            margin-top: 0;
            background: transparent;
        }

        .block-auth-user-leaderboard__header {
            .has-logged-in-user & {
                color: var(--c-primary);
            }
        }

        &:after {
            .has-logged-in-user & {
                background-color: var(--c-secondary);
            }
        }
    }

    @include tablet {
        border: none;
        row-gap: 0;

        &:not(:last-child) {
            &:nth-child(even) {
                background: transparent;
            }

            &:nth-child(even):after {
                background-color: var(--c-grey--light);
            }
        }

    }

    .has-no-logged-in-user &:nth-child(even):after {
        background-color: var(--c-grey--light);
    }

}

.block-auth-user-leaderboard__list-column {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    column-gap: 10px;
    row-gap: 10px;

    @include tablet {
        flex-direction: column;
        grid-column-end: span var(--column-width);
    }
}

.list-leaderboard-move, /* apply transition to moving elements */
.list-leaderboard-enter-active,
.list-leaderboard-leave-active {
    transition: all 0.5s ease;
}

.list-leaderboard-enter-from,
.list-leaderboard-leave-to {
    opacity: 0;
}

/* ensure leaving items are taken out of layout flow so that moving
   animations can be calculated correctly. */
.list-leaderboard-leave-active {
    position: absolute;
}
</style>
